import { createSelector } from "@reduxjs/toolkit";
import { MAP_TYPES_PRICES } from "constants/map-types-prices";
import { MAP_TYPES_OPTIONS } from "../../constants/map-types-options";

export const selectProjectRoot = (state) => state.project;

export const selectProjectsTypes = createSelector(
  selectProjectRoot,
  (project) => project.projectsTypes
);

export const selectProjectsTypeActive = createSelector(
  selectProjectRoot,
  (project) => project.projectTypeActive
);

export const selectProjectsTypeActiveHash = createSelector(
  selectProjectRoot,
  (project) =>
    project.projectTypeActiveHash.length
      ? "#" + project.projectTypeActiveHash
      : ""
);

export const selectProjectsDetails = createSelector(
  selectProjectRoot,
  (project) => project.projectsDetails
);

export const selectProjectDetails = createSelector(
  selectProjectsTypeActive,
  selectProjectsDetails,
  (projectTypeActive, projectsDetails) => projectsDetails[projectTypeActive]
);

export const selectProjectsNavigation = createSelector(
  selectProjectsTypes,
  selectProjectsDetails,
  (projectsTypesList, projectsDetailsList) =>
    projectsTypesList
      .filter(
        (projectType) =>
          projectsDetailsList[projectType] &&
          !projectsDetailsList[projectType].isHidden
      )
      .map((projectType) => ({
        projectType: projectType,
        linkName: projectsDetailsList[projectType]?.linkName || "",
        linkIcon: projectsDetailsList[projectType]?.linkIcon || "",
        linkPath:
          projectsDetailsList[projectType]?.linkPathParent +
          projectsDetailsList[projectType]?.linkPath,
        linkPathChildren: projectsDetailsList[projectType]?.checklistTabs
          ?.length
          ? "/" + projectsDetailsList[projectType]?.checklistTabs[0].link
          : "",
        linkCustomClass:
          projectsDetailsList[projectType]?.linkCustomClass || "",
        isHiddenNavigation:
          projectsDetailsList[projectType]?.isHiddenNavigation || "",
        isHiddenInNavigationButActive:
          projectsDetailsList[projectType]?.isHiddenInNavigationButActive || "",
        relatedPaths: projectsDetailsList[projectType]?.relatedPaths || [],
      }))
);

export const selectChecklist = createSelector(
  selectProjectRoot,
  (project) => project.checklist || []
);

export const selectOptions = createSelector(
  selectProjectRoot,
  (project) => project.checklistOptions || []
);

export const selectOptionsRest = createSelector(selectOptions, (options) =>
  options.filter(
    (option) =>
      option.priceType !== MAP_TYPES_PRICES.PerHour &&
      option.priceType !== MAP_TYPES_PRICES.Count &&
      option.priceType !== MAP_TYPES_PRICES.Single &&
      option.priceType !== MAP_TYPES_PRICES.None &&
      option.priceType !== MAP_TYPES_PRICES.Asap &&
      option.priceValue !== 0
  )
);

export const selectOptionsNoPrice = createSelector(selectOptions, (options) =>
  options.filter((option) => option.isOptionNoPriceForCart === true)
);

export const selectOptionsNoPriceIsExists = createSelector(
  selectOptions,
  (options) =>
    !!options.filter((option) => option.isOptionNoPriceForCart === true).length
);

export const selectOptionsWithPrice = createSelector(
  selectOptions,
  (options) => !!options.filter((option) => option.priceValue).length
);

export const selectOptionsPlus = createSelector(selectOptions, (options) =>
  options.filter((option) => option.priceType === MAP_TYPES_PRICES.Sum)
);

export const selectOptionsPlusTotal = createSelector(
  selectOptionsPlus,
  (options) =>
    options.reduce(
      (accumulator, currentValue) => accumulator + currentValue.priceValue,
      0
    )
);

export const selectOptionsPerHour = createSelector(selectOptions, (options) =>
  options.filter((option) => option.priceType === MAP_TYPES_PRICES.PerHour)
);

export const selectOptionsPercent = createSelector(selectOptions, (options) =>
  options.filter((option) => option.priceType === MAP_TYPES_PRICES.Percent)
);

export const selectOptionsPercentTotal = createSelector(
  selectOptionsPercent,
  (options) =>
    options.reduce(
      (accumulator, currentValue) => accumulator + currentValue.priceValue,
      0
    )
);

export const selectOptionsEmailSignature = createSelector(
  selectOptions,
  (options) =>
    options.filter(
      (option) => option.priceTypeNew === MAP_TYPES_PRICES.EmailSignature
    )
);

export const selectOptionsPagesIsExists = createSelector(
  selectChecklist,
  (checklist) => {
    const pagesOptionsTypes = [
      MAP_TYPES_OPTIONS.InputNumber,
      MAP_TYPES_OPTIONS.InputNumberBanner,
      MAP_TYPES_OPTIONS.InputNumberSizes,
    ];
    return checklist.some((data) =>
      pagesOptionsTypes.includes(data.optionsType)
    );
  }
);

export const selectOptionsPages = createSelector(selectOptions, (options) =>
  options.filter((option) => option.priceType === MAP_TYPES_PRICES.Count)
);

export const selectOptionsPagesTotal = createSelector(
  selectOptionsPages,
  (options) =>
    options.reduce(
      (accumulator, currentValue) => accumulator + currentValue.total,
      0
    )
);

export const selectOptionsAsap = createSelector(selectOptions, (options) =>
  options.filter((option) => option.priceIsAsap)
);

export const selectPayments = createSelector(
  selectProjectRoot,
  (project) => project.payments
);
